import {useHistory, useLocation} from "react-router-dom";
import H from "history";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {tuple} from "~/types";
import moment, {Moment} from "moment";
import "moment/locale/ru";


export function compareByField<T>(fieldName: keyof T) {
    return (a: T, b: T) => {
        return a[fieldName] < b[fieldName] ? -1 : 1;
    }
}


export function distinctByField<E extends object>(array: E[], fieldName: keyof E): E[] {
    const keys = new Set();
    return array.filter(item => {
        if (keys.has(item[fieldName])) {
            return false;
        }
        keys.add(item[fieldName]);
        return true;
    })
}


export const useElementSize = <T extends HTMLElement>() => {
    const [size, setSize] = useState([0, 0]);
    const nodeRef = useRef<T>(null);
    const getSize = (node: T) => {
        return node ? [node.clientWidth, window.innerHeight - node.offsetTop] : [0, 0];
    }

    const ref: (node: T) => void = useCallback((node: T) => {
        setSize(getSize(node));
        nodeRef.current = node;
    }, []);

    const handleResize = () => {
        setSize(getSize(nodeRef.current));
    }

    useEffect(() => {
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [])

    return [size, ref];
}



type useHistoryStateOptions<T> = {
    name?: string
    serialize?: (state: T) => string
    deserialize?: (value: string) => T
    handleBack?: boolean
}
let historyStateBackSemaphore = 0;
let historyPromise = Promise.resolve();

export function useHistoryState<S = string>(options?: useHistoryStateOptions<S>) {
    const handleBack = typeof options?.handleBack == "boolean" ? options.handleBack : true;
    const key = useMemo(() => {
        return options?.name ? options.name : Math.random().toString(36).substring(7);
    }, []);
    const serialize = useMemo(() => options?.serialize ? options.serialize : value => value, []);
    const deserialize = useMemo(() => options?.deserialize ? options.deserialize : value => value, []);

    const history = useHistory();
    const $state = useRef<string>();
    const [value, setNextValue] = useState<S>(() => {
        const params = new URLSearchParams(history.location.search);
        const nextState = params.get(key);
        $state.current = nextState;
        return params.has(key) ? deserialize(nextState) : undefined;
    });

    // не используется useLocation т.к. при любом обновлении адресной строки происходит перерисовка всех компонентов использующих этот хук
    useEffect(() => {
        const handleLocationChange = location => {
            const params = new URLSearchParams(location.search);
            const nextState = params.get(key);
            if (nextState != $state.current) {
                $state.current = nextState;
                setNextValue(params.has(key) ? deserialize(nextState) : undefined);
            }
        }

        handleLocationChange(history.location);
        return history.listen(location => {
            handleLocationChange(location);
        });
    }, [history, key, deserialize, setNextValue]);

    const setValue = useCallback((value: S) => {
        return historyPromise = historyPromise.then(() => {
            // console.log("START CHANGE", key);
            const params = new URLSearchParams(history.location.search);
            const method: keyof H.History = params.has(key) || !handleBack ? "replace" : "push";
            params.set(key, serialize(value));
            // console.log("[SET] ", method, key, params.toString());

            historyStateBackSemaphore && console.warn("Изменение состояния до завершения удаления состояния приводит к неверной работе");

            history[method]({
                ...history.location,
                search: params.toString()
            });
        }).then(() => {
            // console.log("END CHANGE", key);
        })
    }, [history, key]);

    const delValue = useCallback(() => {
        return historyPromise = historyPromise.then(() => {
            // console.log("START CHANGE", key);
            historyStateBackSemaphore && console.warn("Изменение состояния до завершения удаления состояния приводит к неверной работе");
            const params = new URLSearchParams(history.location?.search);
            if (params.has(key)) {
                if (handleBack) {
                    // console.log("[DELETE] goBack", key, params.toString());
                    history.goBack();
                    historyStateBackSemaphore += 1;
                    return new Promise(resolve => {
                        const dispose = history.listen((_, action) => {
                            // console.log("LISTENER", key, action, history.location.search);
                            if (action == "POP") {
                                dispose();

                                // const params = new URLSearchParams(history.location.search);
                                params.delete(key);
                                const location = {
                                    ...history.location,
                                    search: params.toString()
                                }

                                // console.log("[DELETE] replace", key, params.toString());
                                historyStateBackSemaphore -= 1;
                                history.replace(location);
                                resolve(undefined);
                            }
                        });
                    });
                } else {
                    params.delete(key);
                    const location = {
                        ...history.location,
                        search: params.toString()
                    }
                    // console.log("[DELETE] replace", key, params.toString());
                    history.replace(location);
                }
            }
        }).then(() => {
            // console.log("END CHANGE", key);
        });
    }, [history, key])

    useEffect(() => {
        return () => {
            delValue().then(() => null);
        }
    }, []);
    // console.log("state", key, value);

    return tuple(value, setValue, delValue);
}


export function useBooleanHistoryState(name?: string) {
    const [state, setState, delState] = useHistoryState({ name });

    const setValue = useCallback(async value => {
        if (value) {
            await setState("true");
        } else {
            await delState();
        }
    }, [setState, delState]);

    return tuple(state == "true", setValue);
}


export function useStorageState(key, storage = sessionStorage, defaultValue) {
    const [value, setValue] = useState(() => storage.getItem(key));
    const state = useMemo(() => JSON.parse(value), [value]);

    const handleStorageEvent = useCallback((e: StorageEvent) => {
        if (e.key == key) {
            setValue(storage.getItem(key));
        }
    }, [key]);

    const setState = useCallback((state) => {
        const value = JSON.stringify(state);
        storage.setItem(key, state);
        setValue(value);
    }, [key, setValue]);

    useEffect(() => {
        window.addEventListener("storage", handleStorageEvent);
        return () => window.removeEventListener("storage", handleStorageEvent);
    }, [handleStorageEvent]);

    return tuple(typeof state == "undefined" ? defaultValue : state, setState);
}



moment.updateLocale("ru", {
    monthsShort : [
        "янв", "фев", "мар", "апр", "мая", "июн",
        "июл", "авг", "сен", "окт", "ноя", "дек"
    ]
})


export function formatDate(date: Moment) {
    return date.locale("ru").format("D MMM YYYY").toLowerCase();
}


export function formatPhone(phone: string) {
    phone = phone.replace(/[^\d]/g, '');
    if (phone.length == 11) {
        phone = phone.substr(1)
    }
    return `+7 ${phone.substr(0, 3)} ${phone.substr(3, 3)} ${phone.substr(6, 2)} ${phone.substr(8, 2)}`;
}

export function formatPrice(value, {alwaysShowSign} = {alwaysShowSign: false}) {
    value = Number(value);
    if (isNaN(value)) {
        return '';
    }

    const intPart = Math.floor(Math.abs(value));
    const floatPart = Math.abs(value) - intPart;
    let sign = value < 0 ? "-" : "";
    if (alwaysShowSign && value > 0) {
        sign = "+";
    }

    let text = '';
    text = String(intPart).split('').reverse()
        .reduceRight((a, v, i) => (i % 3 == 0 && i > 0 ? [' ', v].concat(a) : [v].concat(a)), [])
        .reverse().join('');
    text += floatPart.toFixed(2).substr(1);

    if (sign) {
        text = sign + " " + text;
    }

    return text;
}